import type { Point2D } from '@utils/geometry.ts'

/**
 * Calculates Euclidean distance between two points.
 */
function euclideanDistance(a: Point2D, b: Point2D): number {
  return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2))
}

/**
 * Finds all neighbor indices within epsilon radius.
 */
function getRegionNeighbors(data: Point2D[], pointIndex: number, epsilon: number): number[] {
  const neighbors: number[] = []
  const p1 = data[pointIndex]
  if (!p1) return neighbors

  for (let i = 0; i < data.length; i++) {
    if (i === pointIndex) continue
    const p2 = data[i]
    if (p2 && euclideanDistance(p1, p2) <= epsilon) {
      neighbors.push(i)
    }
  }
  return neighbors
}

/**
 * Expands the cluster from a core point.
 */
function expandCluster(
  data: Point2D[],
  labels: (number | undefined)[],
  pointIndex: number,
  neighborIndices: number[],
  clusterId: number,
  epsilon: number,
  minPoints: number
): void {
  labels[pointIndex] = clusterId

  // We use a specific index to iterate so we can grow the array dynamically
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let i = 0; i < neighborIndices.length; i++) {
    const neighborIndex = neighborIndices[i]
    if (neighborIndex === undefined) continue

    // If previously marked as noise, it's now a border point of this cluster
    if (labels[neighborIndex] === -1) {
      labels[neighborIndex] = clusterId
    }

    // If already visited (and not noise), skip it
    if (labels[neighborIndex] !== undefined) {
      continue
    }

    // Mark as visited/part of cluster
    labels[neighborIndex] = clusterId

    // Check neighbors of this neighbor
    const currentNeighbors = getRegionNeighbors(data, neighborIndex, epsilon)

    // If it's a core point, add its neighbors to the queue to be explored
    if (currentNeighbors.length >= minPoints - 1) { // -1 because we don't count self in getRegionNeighbors
      neighborIndices.push(...currentNeighbors)
    }
  }
}

/**
 * Main DBSCAN Function
 * @param data - Array of 2D points to cluster

 * @param options
 * @returns Array of clusters, where each cluster is an array of Point2D
 */
function dbscan<T extends Point2D>(
  data: T[],
  options: {
    maxDistance: number
    minPoints: number
  }
): T[][] {
  const labels = new Array(data.length).fill(undefined) as (number | undefined)[]
  let clusterId = -1 // Start before 0

  for (let i = 0; i < data.length; i++) {
    // Skip if already processed
    if (labels[i] !== undefined) continue

    const neighbors = getRegionNeighbors(data, i, options.maxDistance)

    // If not enough neighbors, mark as Noise (-1) temporarily
    // (might be reassigned later if found by another cluster)
    if (neighbors.length < options.minPoints - 1) {
      labels[i] = -1
    } else {
      clusterId++
      expandCluster(data, labels, i, neighbors, clusterId, options.maxDistance, options.minPoints)
    }
  }

  const clusters: T[][] = []

  // Initialize arrays for each cluster
  for (let i = 0; i <= clusterId; i++) {
    clusters.push([])
  }

  for (let i = 0; i < data.length; i++) {
    const label = labels[i]
    if (label !== undefined && label !== -1) {
      const point = data[i]
      if (clusters[label] && point) {
        clusters[label].push(point)
      }
    }
  }

  return clusters
}

export default dbscan